import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PeFormInstanceDependenciesApiModel } from '../api-models/pe-features/PeFormInstanceDependenciesApiModel';

import {
  PeFormInstanceApiModel,
  SearchResultApiModel,
  CreateFormInstanceApiModel,
  PeFormInstanceSelectorsApiModel,
  ProcessPeFormStepApiModel,
  IncidentSummaryApiModel,
  UserListApiModel,
  UserTrainingCourseClassApiModel,
  FormInstanceCollectionDeleteResultApiModel
} from '@app/api/api-models/';
import { ProcessPeFormStepResultApiModel } from '@app/api/api-models/pe-features/ProcessPeFormStepResultApiModel';
import { CheckDateRangeApiModel } from '@app/api/api-models/pe-features/CheckDateRangeApiModel';
import { CheckDateRangeResultApiModel } from '@app/api/api-models/pe-features/CheckDateRangeResultApiModel';
import { PeFormStartCheckApiModel } from '@app/api/api-models/pe-features/PeFormStartCheckApiModel';
import { SummaryPeFormInstanceApiModel } from '@app/api/api-models/pe-features/SummaryPeFormInstanceApiModel';
import { PeFormInstanceFormSummaryApiModel } from '@app/api/api-models/pe-features/PeFormInstanceFormSummaryApiModel';
import { FormInstanceSubjectNarrativeApiModel } from '@app/api/api-models/form-instance/FormInstanceSubjectNarrativeApiModel';
import { UserGoalApiModel } from '@app/api/api-models/goals/UserGoalApiModel';
import { SnapshotRefreshStatusApiModel } from '@app/api/api-models/pe-features/SnapshotRefreshStatusApiModel';
import { SnapshotAboutAndInterestsApiModel } from '@app/api/api-models/pe-features/SnapshotAboutAndInterestsApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { sanitizeHtml } from '@shared/utils/editor-utils';
import { sanitizePipRichText } from '@app/api/api-models/program/pip';

@Injectable()
export class PeFormInstanceService {
  public apiUrl = 'pe-form-instances';

  constructor(private readonly http: HttpClientService, private readonly domSanitizer: DomSanitizer) {}

  public getIncidents(formInstanceId: number): Observable<IncidentSummaryApiModel[]> {
    return this.http
      .get<IncidentSummaryApiModel[]>(`${this.apiUrl}/${formInstanceId}/incidents`)
      .pipe(map((array) => array.map((x) => new IncidentSummaryApiModel().deserialize(x))));
  }

  public getFormsSummary(formInstanceId: number): Observable<PeFormInstanceFormSummaryApiModel[]> {
    return this.http
      .get<PeFormInstanceFormSummaryApiModel[]>(`${this.apiUrl}/${formInstanceId}/forms-summary`)
      .pipe(map((array) => array.map((x) => new PeFormInstanceFormSummaryApiModel().deserialize(x))));
  }

  public get(id: number): Observable<PeFormInstanceApiModel> {
    return this.http
      .get<PeFormInstanceApiModel>(`${this.apiUrl}/get/${id}`)
      .pipe(map((x) => new PeFormInstanceApiModel().deserialize(x, this.domSanitizer)));
  }

  public getNextUsers(id: number): Observable<Array<UserListApiModel>> {
    return this.http.get<Array<UserListApiModel>>(`${this.apiUrl}/${id}/next-users`);
  }

  public add(model: CreateFormInstanceApiModel): Observable<PeFormInstanceApiModel> {
    return this.http.post<PeFormInstanceApiModel>(`${this.apiUrl}/add`, model);
  }

  public update(model: PeFormInstanceApiModel): Observable<PeFormInstanceApiModel> {
    model.CurrentComment = sanitizeHtml(model.CurrentComment);
    model.CreatorNarrative = sanitizeHtml(model.CreatorNarrative);
    sanitizePipRichText(model.Pip);
    return this.http.put<PeFormInstanceApiModel>(`${this.apiUrl}/update`, model);
  }

  public getSelectors(formInstanceId: number): Observable<PeFormInstanceSelectorsApiModel> {
    return this.http.get<PeFormInstanceSelectorsApiModel>(`${this.apiUrl}/${formInstanceId}/selectors`);
  }

  public deleteById(id: number): Observable<Object> {
    return this.http.delete(`${this.apiUrl}/delete/${id}`);
  }

  public deleteMultiple(ids: Array<number>): Observable<FormInstanceCollectionDeleteResultApiModel> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.delete(`${this.apiUrl}/delete-multiple`, { params: queryParams });
  }

  public processStep(model: ProcessPeFormStepApiModel): Observable<ProcessPeFormStepResultApiModel> {
    model.Comment = sanitizeHtml(model.Comment);

    return this.http.put<ProcessPeFormStepResultApiModel>(`${this.apiUrl}/process-step`, model);
  }

  public getSimilarNotSubmitted(
    formId: number,
    programRoleId: string | null = null,
    userId: string | null = null
  ): Observable<Array<SearchResultApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formId: formId.toString(),
        toId: programRoleId ? programRoleId : '',
        toUserId: userId ? userId : ''
      }
    });

    return this.http.get<Array<SearchResultApiModel>>(`${this.apiUrl}/not-submitted`, { params: params });
  }

  public getStartCheck(formId: number, programRoleId: string): Observable<PeFormStartCheckApiModel> {
    const params = new HttpParams({ fromObject: { formId: formId.toString(), toId: programRoleId } });

    return this.http.get<PeFormStartCheckApiModel>(`${this.apiUrl}/start-check`, { params: params });
  }

  public checkUsedDateRange(model: CheckDateRangeApiModel): Observable<CheckDateRangeResultApiModel> {
    return this.http.post<CheckDateRangeResultApiModel>(`${this.apiUrl}/check-used-date-range`, model);
  }

  public getSummariesById(formInstanceId: number): Observable<Array<SummaryPeFormInstanceApiModel>> {
    const params = new HttpParams({
      fromObject: {
        formInstanceId: formInstanceId.toString()
      }
    });

    return this.http.get<Array<SummaryPeFormInstanceApiModel>>(`${this.apiUrl}/summaries`, { params: params });
  }

  public getSummariesByIdsMultiple(ids: Array<number>): Observable<Array<PeFormInstanceDependenciesApiModel>> {
    let queryParams = new HttpParams();
    queryParams = queryParams.appendAll({ ids: ids });

    return this.http.get<Array<PeFormInstanceDependenciesApiModel>>(`${this.apiUrl}/summaries-multiple`, {
      params: queryParams
    });
  }

  public updateSubjectNarrative(
    formInstanceId: number,
    narrative: FormInstanceSubjectNarrativeApiModel
  ): Observable<FormInstanceSubjectNarrativeApiModel> {
    return this.http.post<FormInstanceSubjectNarrativeApiModel>(
      `${this.apiUrl}/${formInstanceId}/update-subject-narrative`,
      narrative
    );
  }

  public getUserAboutAndInterests(formInstanceId: number): Observable<SnapshotAboutAndInterestsApiModel> {
    return this.http.get<SnapshotAboutAndInterestsApiModel>(`${this.apiUrl}/${formInstanceId}/user-about-interests`);
  }

  public getUserGoals(formInstanceId: number): Observable<UserGoalApiModel[]> {
    return this.http.get<UserGoalApiModel[]>(`${this.apiUrl}/${formInstanceId}/user-goals`);
  }

  public getTrainingCourseClasses(formInstanceId: number): Observable<UserTrainingCourseClassApiModel[]> {
    return this.http.get<UserTrainingCourseClassApiModel[]>(`${this.apiUrl}/${formInstanceId}/training-course-classes`);
  }

  public getSnapshotRefreshStatus(formInstanceId: number): Observable<SnapshotRefreshStatusApiModel> {
    return this.http.get<SnapshotRefreshStatusApiModel>(`${this.apiUrl}/${formInstanceId}/snapshot-refresh-status`);
  }

  public refreshSnapshot(formInstanceId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${formInstanceId}/refresh-snapshot`, null);
  }

  public getPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/pdf`, { responseType: 'blob' });
  }
}
